"use client"

import { useCallback, type ReactElement } from "react"
import { toast } from "sonner"

// Phase 38 / Plan 38-01: Reusable toast + audio chime primitive extracted
// verbatim from Phase 37's appointment-reminder-scheduler.tsx. Two consumers:
//   1. <AppointmentReminderScheduler /> (Phase 37)
//   2. <MetaLeadNotifier /> (Phase 38, Plan 38-05)
//
// Signature was LOCKED by 38-CONTEXT.md D-19 and 38-UI-SPEC.md; `onDismiss`
// was added past that lock because persistent reminder dismissal (2026-07-27)
// needs to observe the user closing the toast — keep any further additions
// equally deliberate.

export interface ToastNotifierOptions {
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  /** Fires when the user dismisses the toast (X / swipe) — NOT on action click. */
  onDismiss?: () => void
  /** Defaults to Phase 37's shared reminder chime asset when omitted. */
  soundUrl?: string
  render?: (id: string | number) => ReactElement
}

export function useToastNotifier(): (options: ToastNotifierOptions) => string | number {
  // useCallback keeps the notify identity stable across renders so consumers
  // (notably Plan 38-05's <MetaLeadNotifier />) can safely include it in a
  // useEffect dependency array without re-arming their polling interval on
  // every render.
  const notify = useCallback((options: ToastNotifierOptions) => {
    const { title, description, actionLabel, onAction, onDismiss, soundUrl, render } = options

    // REM-03, REM-04: sticky toast with title + description + optional action.
    // Mirrors the call shape from appointment-reminder-scheduler.tsx:51-59.
    const toastId = render ? toast.custom(render, {
      duration: Infinity,
      dismissible: true,
      onDismiss: onDismiss ? () => onDismiss() : undefined,
    }) : toast(title, {
      description,
      duration: Infinity,
      dismissible: true,
      onDismiss: onDismiss ? () => onDismiss() : undefined,
      action:
        actionLabel && onAction
          ? { label: actionLabel, onClick: onAction }
          : undefined,
    })

    // REM-05, REM-06: audio chime with swallowed failure. Verbatim extraction
    // from appointment-reminder-scheduler.tsx:62-73 — autoplay policies,
    // missing asset, and transient decode errors are intentionally ignored.
    try {
      const audio = new Audio(soundUrl ?? "/sounds/reminder.mp3")
      audio.volume = 0.6
      void audio.play().catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[useToastNotifier] audio play rejected:", err)
        }
      })
    } catch {
      /* ignore — new Audio() shouldn't throw, but belt and suspenders */
    }

    return toastId
  }, [])

  return notify
}
